import { AxiosInstance } from 'axios'
import {
  ApprovalDateRangeFilterInAPI,
  DateRangeFilterInAPI,
  DeleteTripsAndExpensesFromReportPayload,
  DeleteTripsAndExpensesFromReportResponse,
  DownloadFileType,
  MoveTeamReportsToPayPeriodParams,
  ReportAction,
  ReportDetail,
  ReportEvent,
  ReportPayPeriod,
  ReportsFilter,
  ReportsFilterInAPI,
  ReportShort,
  UpdateTeamReportsParams
} from 'types/reports'
import { Group, Team } from 'types/groups'
import { REPORTS_ENDPOINTS } from 'api/ApiRoutes'
import {
  cleanObject,
  formatDateParams,
  getTeamReportParams
} from 'utils/Functions'
import { TripDetailed, TripShort } from 'types/trips'
import { TransactionDetailed, TransactionShort } from 'types/transactions'
import { PaginatedMetadataResult } from 'types/utility'
import pickBy from 'lodash.pickby'
import { BulkInviteResponse } from 'types/invitations'

class ReportsApi {
  constructor(private axiosInstance: AxiosInstance) {}

  getReport = ({
    reportId,
    filters
  }: {
    reportId: ReportShort['token_id']
    filters: {
      teamId?: Group['token_id']
    }
  }) => {
    return filters?.teamId
      ? this.axiosInstance
          .get<ReportDetail>(
            REPORTS_ENDPOINTS.TEAM_REPORT(filters.teamId, reportId)
          )
          .then((d) => d.data)
      : this.axiosInstance
          .get<ReportDetail>(REPORTS_ENDPOINTS.REPORT(reportId))
          .then((d) => d.data)
  }

  updateReport = async ({
    reportId,
    teamId,
    data
  }: {
    reportId: ReportShort['token_id']
    teamId?: Group['token_id'] | Team['token_id']
    data:
      | { action: ReportAction; comment?: string; occurred_at?: string }
      | Partial<ReportDetail>
  }) => {
    const isUpdatingStatus = 'action' in data
    const body = isUpdatingStatus
      ? {
          report: {
            action: data.action.toLowerCase().replace(/\s/g, '_'),
            body: data.comment,
            occurred_at: data.occurred_at
          }
        }
      : {
          report: data
        }

    const reportPath = isUpdatingStatus
      ? REPORTS_ENDPOINTS.REPORT_STATUS(reportId)
      : REPORTS_ENDPOINTS.REPORT(reportId)
    const teamReportPath = isUpdatingStatus
      ? REPORTS_ENDPOINTS.TEAM_REPORT_STATUS(teamId ?? '', reportId)
      : REPORTS_ENDPOINTS.TEAM_REPORT(teamId ?? '', reportId)

    const d = await this.axiosInstance.put<ReportDetail>(
      teamId ? teamReportPath : reportPath,
      body
    )
    return d.data
  }

  deleteReport = ({ reportId }: { reportId: ReportShort['token_id'] }) => {
    return this.axiosInstance.delete(REPORTS_ENDPOINTS.REPORT(reportId))
  }

  createReport = async ({
    name = null,
    payPeriod = null,
    forceCreate = false
  }: {
    name: string | null
    payPeriod: string | null
    forceCreate?: boolean
  }) => {
    const d = await this.axiosInstance.post<ReportShort>(
      REPORTS_ENDPOINTS.BASE,
      {
        report: {
          name,
          pay_period_token_id: payPeriod,
          create_anyway: forceCreate
        }
      }
    )
    return d.data
  }

  getReports = async ({
    limit = 10,
    starting_after = null,
    status = null,
    name = null
  }: {
    limit?: number
    starting_after?: string | null
    status?: string | null
    name?: string | null
  }) => {
    const d = await this.axiosInstance.get<ReportShort[]>(
      REPORTS_ENDPOINTS.BASE,
      {
        params: cleanObject({ limit, starting_after, status, name })
      }
    )
    return d.data
  }

  detachFromReport = async ({
    reportId,
    kind,
    id
  }: {
    reportId: ReportShort['token_id']
    kind: 'trips' | 'transactions'
    id: TripShort['token_id'] | TransactionShort['token_id']
  }) => {
    const d = await this.axiosInstance.delete<{
      report: ReportDetail
      transaction?: TransactionDetailed
      trip?: TripDetailed
    }>(REPORTS_ENDPOINTS.DETACH_FROM_REPORT(reportId, kind, id))
    return d.data
  }

  getPaginatedTeamReports = async ({
    teamId,
    filters = { status: 'all' },
    page_size,
    page
  }: {
    teamId: Group['token_id']
    filters: ReportsFilter
    page_size: number
    page: number
  }) => {
    const _filters = getTeamReportParams(filters)
    const { data } = await this.axiosInstance.get<
      PaginatedMetadataResult<ReportShort> & {
        metadata: { total_flagged_results_count: number }
      }
    >(REPORTS_ENDPOINTS.TEAM_REPORTS(teamId), {
      params: {
        ...cleanObject(_filters),
        paginate: 'by_page',
        page_size,
        page
      }
    })
    return data
  }

  updateTeamReports = async ({
    teamId,
    params
  }: {
    teamId: Team['token_id']
    params: UpdateTeamReportsParams
  }) => {
    const { approved_date_range, submission_date_range, ...rest } =
      params.filter_params
    const submissionDateRange = formatDateParams<DateRangeFilterInAPI>(
      submission_date_range
    )
    const approvedDateRange = formatDateParams<ApprovalDateRangeFilterInAPI>(
      approved_date_range,
      'approved_'
    )
    const transformedFilters: ReportsFilterInAPI = {
      ...rest,
      ...approvedDateRange,
      ...submissionDateRange
    }

    params.filter_params = cleanObject(transformedFilters)
    const { data } = await this.axiosInstance.post<{
      message: string
      result: string
    }>(REPORTS_ENDPOINTS.TEAM_REPORT_BATCH_STATUS_UPDATE(teamId), params)
    return data
  }

  deleteTripsAndExpensesFromReport = async ({
    team_id,
    report_id,
    ...payload
  }: DeleteTripsAndExpensesFromReportPayload) => {
    const { data } =
      await this.axiosInstance.delete<DeleteTripsAndExpensesFromReportResponse>(
        team_id
          ? REPORTS_ENDPOINTS.TEAM_REPORT_REJECT_ITEMS(team_id, report_id)
          : REPORTS_ENDPOINTS.REPORT_REMOVE_ITEMS(report_id),
        {
          data: payload
        }
      )
    return data
  }

  moveReportsToPayPeriod = async ({
    teamId,
    params
  }: {
    teamId: Team['token_id']
    params: MoveTeamReportsToPayPeriodParams
  }) => {
    const { data } = await this.axiosInstance.post<{
      reports_updated: number
    }>(REPORTS_ENDPOINTS.TEAM_REPORT_BATCH_MOVE_PAY_PERIOD(teamId), params)
    return data
  }

  getReportEvents = async ({
    reportId,
    startingAfter,
    teamId,
    limit = 6
  }: {
    reportId: ReportShort['token_id']
    startingAfter?: ReportEvent['token_id']
    limit?: number
    teamId?: Group['token_id']
  }) => {
    const params = {
      limit,
      starting_after: startingAfter
    }
    const { data } = await this.axiosInstance.get<{
      report_events: ReportEvent[]
      total_results_count: number
    }>(
      teamId
        ? REPORTS_ENDPOINTS.TEAM_REPORT_EVENTS(teamId, reportId)
        : REPORTS_ENDPOINTS.REPORT_EVENTS(reportId),
      {
        params
      }
    )
    return data
  }

  postReportComment = async ({
    reportId,
    teamId,
    asGuest,
    body,
    name
  }: {
    teamId?: Group['token_id']
    reportId: ReportShort['token_id']
    body: string
    name?: string
    asGuest?: boolean
  }) => {
    const reportPath = asGuest
      ? REPORTS_ENDPOINTS.REPORT_GUEST_COMMENT(reportId)
      : REPORTS_ENDPOINTS.REPORT_COMMENT(reportId)
    const teamReportPath = asGuest
      ? REPORTS_ENDPOINTS.TEAM_REPORT_GUEST_COMMENT(teamId ?? '', reportId)
      : REPORTS_ENDPOINTS.TEAM_REPORT_COMMENT(teamId ?? '', reportId)

    const { data } = await this.axiosInstance.post(
      teamId ? teamReportPath : reportPath,
      asGuest ? { body, name } : { report_event: { body } }
    )
    return data
  }

  downloadReport = async ({
    teamId,
    reportId,
    params,
    deliveryMode = 'email'
  }: {
    teamId?: Group['token_id']
    reportId: ReportShort['token_id']
    deliveryMode?: 'email' | 'direct'
    params: {
      file_type: DownloadFileType
      include_attachments?: boolean
    }
  }) => {
    const { data } = await this.axiosInstance.get(
      teamId
        ? REPORTS_ENDPOINTS.TEAM_REPORT_DOWNLOAD(teamId, reportId)
        : REPORTS_ENDPOINTS.REPORT_DOWNLOAD(reportId),
      {
        responseType: deliveryMode === 'direct' ? 'blob' : 'json',
        params:
          deliveryMode === 'direct'
            ? {
                ...params,
                method: 'direct',
                reponseType: 'blob'
              }
            : params
      }
    )
    if (deliveryMode === 'direct') {
      const blob = new Blob([data], { type: params.file_type })
      const objectUrl = URL.createObjectURL(blob)

      const aEl = document.createElement('a')
      aEl.href = objectUrl
      aEl.download = `Report_${reportId}.${params.file_type === 'excel' ? 'xlsx' : params.file_type}`
      document.body.appendChild(aEl)
      aEl.click()
      aEl.remove()
      URL.revokeObjectURL(objectUrl)
    }
    return data
  }

  shareReport = async ({
    teamId,
    reportId,
    emails,
    comment
  }: {
    teamId?: Group['token_id']
    reportId: ReportShort['token_id']
    emails: string[]
    comment?: string
  }) => {
    const { data } = await this.axiosInstance.post(
      teamId
        ? REPORTS_ENDPOINTS.TEAM_REPORT_SHARE(teamId, reportId)
        : REPORTS_ENDPOINTS.REPORT_SHARE(reportId),
      {
        report: { emails, note: comment }
      }
    )
    return data
  }

  downloadTeamReportsBatch = async (
    params: UpdateTeamReportsParams['filter_params'] & {
      file_type: DownloadFileType
      include_trips?: boolean
      include_transactions?: boolean
    }
  ) => {
    const { submission_date_range, approved_date_range, ...rest } = params ?? {}

    const submissionDateRange = formatDateParams<DateRangeFilterInAPI>(
      submission_date_range
    )
    const approvedDateRange = formatDateParams<ApprovalDateRangeFilterInAPI>(
      approved_date_range,
      'approved_'
    )

    const cleanParams = pickBy(
      {
        ...rest,
        ...approvedDateRange,
        ...submissionDateRange
      },
      (v) => v !== null && v !== '' && v !== undefined
    )
    const { data } = await this.axiosInstance.get<{ file_name: string }>(
      REPORTS_ENDPOINTS.TEAM_REPORTS_BATCH_DOWNLOAD,
      {
        params: cleanParams
      }
    )
    return data
  }

  getReportPayPeriods = async () => {
    const d = await this.axiosInstance.get<ReportPayPeriod[]>(
      REPORTS_ENDPOINTS.REPORT_PAY_PERIODS
    )
    return d.data
  }

  uploadCsvBulkReportUpdate = async ({
    file,
    teamId
  }: {
    file: File
    teamId: Group['token_id']
  }) => {
    const formData = new FormData()
    formData.append('bulk_report_update[data_file]', file)
    formData.append('bulk_report_update[root_team_token_id]', teamId as string)

    const d = await this.axiosInstance.post<BulkInviteResponse>(
      REPORTS_ENDPOINTS.TEAM_REPORT_BULK_UPDATE(teamId),
      formData
    )
    return d.data
  }

  getReportCSVTemplate = async ({ teamId }: { teamId: Group['token_id'] }) => {
    const d = await this.axiosInstance.post(
      REPORTS_ENDPOINTS.TEAM_REPORT_BULK_UPDATE_TEMPLATE(teamId)
    )
    return d.data
  }

  getReportsSummary = async ({ teamId }: { teamId: Team['token_id'] }) => {
    const d = await this.axiosInstance.get(
      REPORTS_ENDPOINTS.TEAM_REPORTS_SUMMARY(teamId)
    )
    return d.data
  }
}

export default ReportsApi
